<template>
    <v-app>
        <v-dialog v-model="showPlanChangeDialog" persistent max-width="700">
            <v-card>
                <v-card-title>
                    Dein Abo wurde erst kürzlich geändert
                </v-card-title>
                <v-card-text class="text-body-1">
                    Du hast dein Abo in den letzten 30 Tagen bereits einmal herabgestuft. 
                    Eine Herabstufung ist nur einmal innerhalb von 30 Tagen möglich. 
                    Um ZEIPSY weiterhin nutzen zu können, führe bitte ein Upgrade auf den nächsthöheren Abo-Plan durch. 
                    Bei Fragen oder Problemen steht dir der Support gerne zur Verfügung.
                </v-card-text>
                <v-card-actions class="px-6 pb-5">
                    <v-spacer></v-spacer>
                    <v-btn :color="$store.state.theme.primary" text @click="reload">
                        Seite neu laden
                    </v-btn>
                    <v-btn outlined :color="$store.state.theme.green" @click="upgrade">
                        Zur Abo-Verwaltung
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-container v-if="session === null" fluid class="justify-center align-center layout">
            <Login v-if="$route.name !== 'passwort-vergessen'" @showError="showError" @showInfo="showInfo" />
            <PasswortVergessen v-else @showError="showError" @showInfo="showInfo" />
        </v-container>
        <div v-else class="layout content-container">
            <NavigationBar
                v-if="$store.state.client.eigenes_schema !== null"
                class="hidden-md-and-down"
                :style="{ background: $store.state.theme.primary }"
                :session="session"
                @showError="showError"
                @showInfo="showInfo"
            >
            </NavigationBar>
            <v-main class="content-container">
                <v-app-bar
                    v-if="$store.state.client.eigenes_schema !== null"
                    dark
                    :color="$store.state.theme.primary"
                    class="hidden-lg-and-up"
                >
                    <v-app-bar-nav-icon dark @click.native="dialog = true"></v-app-bar-nav-icon>
                    <v-toolbar-title>Menü</v-toolbar-title>
                    <v-spacer></v-spacer>

                    <v-tooltip bottom v-if="$store.getters.isInTrialPeriod && !($store.state.client.beta_circle === true) && !$store.getters.hasPaidSubscription" open-delay="300">
                        <template v-slot:activator="{ on, attrs }" >
                            <v-badge color="orange" overlap>
                                <template v-slot:badge>
                                    {{ $store.getters.remainingTrialDays }}
                                </template>
                                <v-chip small color="white" text-color="black" @click="upgrade" v-bind="attrs" v-on="on" :disabled="opening_customer_portal">
                                    <v-icon v-if="!opening_customer_portal" left small>mdi-account-cog</v-icon>
                                    <v-progress-circular class="mr-1" size="15" width="2" indeterminate v-else />
                                    Probezeit:&nbsp;<span class="font-weight-medium">{{ $store.getters.subscriptionTier }}</span>
                                    <v-icon right small class="ml-1">mdi-chevron-right</v-icon>
                                </v-chip>
                            </v-badge>
                        </template>
                        <p class="pb-0 mb-0">
                            Du kannst noch {{ $store.getters.remainingTrialDays }} Tage alle Funktionen uneingeschränkt testen.<br/>
                            Danach wird dein Zugang automatisch auf die kostenlose Version (Geringfügig) umgestellt.<br/>
                            Außerdem kannst du hier unsere Abos einsehen und upgraden.
                        </p>
                    </v-tooltip>
                    <div v-else :class="'gradient-border-container-'+$store.getters.subscriptionTier">
                        <v-tooltip bottom open-delay="300">
                            <template v-slot:activator="{ on, attrs }" >
                                <v-chip class="gradient-chip" small color="white" text-color="black" @click="upgrade" v-bind="attrs" v-on="on" :disabled="opening_customer_portal">
                                    <v-icon v-if="!opening_customer_portal" left small>mdi-account-cog</v-icon>
                                    <v-progress-circular class="mr-1" size="15" width="2" indeterminate v-else />
                                    Abo-Details:&nbsp;<span class="font-weight-medium">{{ $store.getters.subscriptionTier }}</span>
                                    <v-icon v-if="!$store.state.client.beta_circle" right small class="ml-1">mdi-chevron-right</v-icon>
                                </v-chip>
                            </template>
                            <p class="pb-0 mb-0" v-if="$store.getters.subscriptionTier !== 'GERINGFÜGIG'">
                                Hier kannst du dein Abo verwalten und deine Rechnungen herunterladen.
                            </p>
                            <p class="pb-0 mb-0" v-else>
                                Hier kannst du unsere Abos einsehen und upgraden.
                            </p>
                        </v-tooltip>
                    </div>

                    <v-btn icon color="white" text-color="black" class="ml-3" @click="assistantDialogVisible = true">
                        <v-icon>mdi-robot-happy-outline</v-icon>
                    </v-btn>

                    <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
                        <v-card>
                            <v-app-bar dark :color="$store.state.theme.primary">
                                <v-toolbar-title>Menü</v-toolbar-title>
                                <v-spacer></v-spacer>
                                <v-btn icon @click.native="dialog = false">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                            </v-app-bar>
                            <NavigationItems @click.native="dialog = false" :session="session" />
                        </v-card>
                    </v-dialog>
                </v-app-bar>
                <router-view
                    v-if="loaded"
                    :class="this.$vuetify.breakpoint.xs ? 'pa-5' : 'pa-5'"
                    :session="session"
                    ref="currentRoute"
                    @showError="showError"
                    @showInfo="showInfo"
                />
                <v-container v-else>
                    <v-skeleton-loader type="table" elevation="1"></v-skeleton-loader>
                </v-container>
            </v-main>
        </div>
        <SupabaseConnector :session="session" ref="sb" />
        <assistant-dialog v-model="assistantDialogVisible" :session="session"></assistant-dialog>
        
        <!-- Auto-logout warning snackbar -->
        <v-snackbar
            v-model="showLogoutWarning"
            :timeout="-1"
            :color="$store.state.theme.orange"
            multi-line
            prominent
        >
            <v-icon left>mdi-clock-alert</v-icon>
            <strong>Automatische Abmeldung</strong><br>
            Du wirst in {{ warningCountdown }} Sekunden automatisch abgemeldet.<br>
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="cancelLogoutWarning">
                    Abbrechen
                </v-btn>
            </template>
        </v-snackbar>
        
        <!-- Auto-logout confirmation snackbar -->
        <v-snackbar
            v-model="showLogoutMessage"
            :timeout="-1"
            :color="$store.state.theme.red"
            multi-line
            prominent
        >
            <v-icon left>mdi-logout</v-icon>
            <strong>Automatisch abgemeldet</strong><br>
            Du wurdest aufgrund von Inaktivität automatisch abgemeldet.
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showLogoutMessage = false">
                    Schließen
                </v-btn>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
import NavigationBar from './components/NavigationBar.vue';
import NavigationItems from './components/NavigationItems.vue';
import Login from './components/Login.vue';
import { supabase } from './supabase';
import SupabaseConnector from './components/SupabaseConnector.vue';
import cipher from './helpers/cipher';
import connector from './helpers/supabase-connector.js';
import dayjs from 'dayjs';
import PasswortVergessen from './components/PasswortVergessen.vue';
import AssistantDialog from './components/AssistantDialog.vue';

export default {
    name: 'App',
    data() {
        return {
            dialog: false,
            loaded: false,
            session: null,
            error: null,
            error_message: '',

            showPlanChangeDialog: false,
            opening_customer_portal: false,
            assistantDialogVisible: false,
            
            // Activity tracking for automatic logout
            activityTimer: null,
            activityEvents: ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'],
            lastActivityTime: Date.now(),
            
            // Warning system for auto-logout
            warningTimer: null,
            warningActive: false,
            warningCountdown: 10,
            showLogoutWarning: false,
            showLogoutMessage: false
        };
    },
    components: { NavigationBar, Login, NavigationItems, SupabaseConnector, PasswortVergessen, AssistantDialog },

    watch: {
        '$route'() {
            this.checkLastPlanChange();
        },
        // Watch for changes in auto-logout settings
        '$store.state.client.auto_logout_minutes'() {
            this.setupActivityTracking();
        }
    },

    mounted() {
        supabase.auth.getSession().then(({ data }) => {
            this.session = data.session;
            this.$store.state.session = this.session;

            if (this.session && 'user' in this.session && 'id' in this.session.user) {
                this.$store.state.client.id = this.session.user.id;
            }

            if (this.session !== null && this.$store.state.client.eigenes_schema === null) {
                // TODO: this reference to 'this' is probably not correct
                connector.getDataOnly(this, 'vwkunden', 'id', true).then((data) => {
                    if (data === -1) {
                        // the error has already been displayed
                        return;
                    }
                    else if (data.length > 0) {
                        this.$store.commit('setClient', data[0]);
                        this.checkLastPlanChange();
                        if (this.$store.state.client.eigenes_schema === null) {
                            // redirect to welcome page
                            if (this.$route.name !== 'welcome') this.$router.push({ name: 'welcome' });
                        }
                    } else {
                        // redirect to welcome page
                        if (this.$route.name !== 'welcome') this.$router.push({ name: 'welcome' });
                    }
                });
            }
        });

        supabase.auth.onAuthStateChange((_, _session) => {
            this.session = _session;
            this.$store.state.session = this.session;

            if (this.session && 'user' in this.session && 'id' in this.session.user) {
                this.$store.state.client.id = this.session.user.id;
                
                // check if provider refresh token is present, if so, store it in local storage
                if (this.session.provider_refresh_token) {
                    localStorage.setItem('provider_refresh_token', this.session.provider_refresh_token);
                    // Ensure AES key is available
                    // if (this.session.user.email === 'contact@zeipsy.com') {
                    //     if (!this.$store.state.aes_key_file) {
                    //         const { aes_key, aes_key_file } = await cipher.getAESKeysSilent(this);
                    //         if (aes_key_file !== null) {
                    //             this.$store.state.aes_key_file = aes_key_file;
                    //         }
                    //     }

                    //     // If we have encryption key, store the token encrypted in the database
                    //     if (this.$store.state.aes_key_file) {
                    //         try {
                    //             // Encrypt the token
                    //             const encryptedToken = await cipher.encryptDataJSON(
                    //                 this.session.provider_refresh_token, 
                    //                 this.$store.state.aes_key_file
                    //             );
                                
                    //             // Store in database
                    //             await connector.update(
                    //                 this,
                    //                 'kunden',
                    //                 { provider_refresh_token: encryptedToken },
                    //                 this.session.user.id
                    //             );
                    //         } catch (error) {
                    //             // Log error but don't display to user
                    //             connector.logError(this, {
                    //                 uid: this.session.user.id,
                    //                 message: 'Failed to encrypt and store provider refresh token: ' + error.message
                    //             });
                    //         }
                    //     }
                    // }
                }
            }

            if (this.session !== null && this.$store.state.client.eigenes_schema === null) {
                // TODO: this reference to 'this' is probably not correct
                connector.getDataOnly(this, 'vwkunden', 'id', true).then((data) => {
                    if (data === -1) {
                        // error has already been shown
                        return;
                    }
                    else if (data.length > 0) {
                        this.$store.commit('setClient', data[0]);
                        this.checkLastPlanChange();
                        if (this.$store.state.client.eigenes_schema === null) {
                            // redirect to welcome page
                            if (this.$route.name !== 'welcome') this.$router.push({ name: 'welcome' });
                        }
                    } else {
                        // redirect to welcome page
                        if (this.$route.name !== 'welcome') this.$router.push({ name: 'welcome' });
                    }
                });
            }
        });

        if (this.$store.state.data_key) {
            cipher.getAESKeys(this).then(() => (this.loaded = true));
        } else {
            // TODO: redirect user to settings page and prompt to enter data key.
            this.loaded = true;
        }

        // Setup activity tracking after initialization
        this.setupActivityTracking();
    },

    beforeDestroy() {
        // Clean up activity tracking when component is destroyed
        this.cleanupActivityTracking();
    },

    methods: {

        reload() {
            window.location.reload();
        },

        checkLastPlanChange() {
            
            if (this.$store.state.client.last_downgrade_on !== null && this.$store.state.client.last_upgrade_on !== null) {
                const last_downgrade_on = dayjs(this.$store.state.client.last_downgrade_on);
                const last_upgrade_on = dayjs(this.$store.state.client.last_upgrade_on);

                // check if last downgrade was after last upgrade and whether the downgrade was within the last 30 days
                if (last_downgrade_on.isAfter(last_upgrade_on) && last_downgrade_on.isAfter(dayjs().subtract(30, 'day'))) {
                    this.showPlanChangeDialog = true;
                    connector.logError(this, {
                        uid: this.$store.state.client.id,
                        message: 'Show frequent plan change dialog'
                    });
                    return;
                }
            }
            this.showPlanChangeDialog = false;
        },

        showError(error) {
            this.$refs.sb.showError(error);
        },

        showInfo(info) {
            this.$refs.sb.showInfo(info);
        },

        // IMPORTANT: ALSO UPDATE THIS IN NavigationBar.vue
        async upgrade() {
            // if (this.$store.state.client.beta_circle) {
                // do nothing
                // return;
            // }

            if (this.$store.state.client.has_stripe_customer_id) {
                // redirect to customer portal
                this.opening_customer_portal = true;
                let function_name = 'create-stripe-customer-portal';
                if (process.env.NODE_ENV === 'development') {
                    function_name = 'create-stripe-customer-portal-test';
                }
                const { data, error } = await supabase.functions.invoke(function_name);

                if (error) {
                    this.opening_customer_portal = false;
                    console.error(error);
                    return;
                }
                this.opening_customer_portal = false;
                window.location.href = data?.url;
            } else {
                this.$router.push('/upgrade');
            }
        },

        // Activity tracking methods for automatic logout
        setupActivityTracking() {
            const client = this.$store.state.client;
            
            // Check if auto-logout is enabled (auto_logout_minutes > 0)
            if (!client || !client.auto_logout_minutes || client.auto_logout_minutes <= 0) {
                this.cleanupActivityTracking();
                return;
            }
            
            // Initialize activity tracking
            this.lastActivityTime = Date.now();
            
            // Set up event listeners for meaningful activity detection
            this.activityEvents.forEach(event => {
                window.addEventListener(event, this.updateActivityTime, { passive: true });
            });
            
            // Add Page Visibility API listener for minimized windows
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
            
            this.startActivityMonitoring();
        },

        updateActivityTime() {
            this.lastActivityTime = Date.now();
            
            // If warning is active, cancel it since user is active
            if (this.warningActive) {
                this.cancelLogoutWarning();
            }
            
            // Always restart monitoring on any activity
            this.startActivityMonitoring();
        },

        startActivityMonitoring() {
            clearTimeout(this.activityTimer);
            
            // Check activity every 5 seconds
            this.activityTimer = setTimeout(() => {
                this.checkForInactivity();
            }, 5000);
        },

        checkForInactivity() {
            const timeoutMinutes = this.$store.state.client.auto_logout_minutes;
            const timeoutMs = timeoutMinutes * 60 * 1000;
            const currentTime = Date.now();
            const timeSinceLastActivity = currentTime - this.lastActivityTime;
            
            // Check if we should show warning (30 seconds before logout)
            if (timeSinceLastActivity >= timeoutMs - 30000) {
                this.startLogoutWarning();
            } else {
                // Continue monitoring
                this.startActivityMonitoring();
            }
        },

        handleVisibilityChange() {
            if (document.hidden) {
                // Page became hidden - record this as last activity time
                this.lastActivityTime = Date.now();
            } else {
                // Page became visible - check if we should logout immediately
                const timeoutMinutes = this.$store.state.client.auto_logout_minutes;
                const timeoutMs = timeoutMinutes * 60 * 1000;
                const currentTime = Date.now();
                const timeSinceLastActivity = currentTime - this.lastActivityTime;
                
                if (timeSinceLastActivity >= timeoutMs) {
                    // User has been away longer than timeout - logout immediately
                    this.performAutoLogout();
                } else if (timeSinceLastActivity >= timeoutMs - 30000) {
                    // User has been away almost timeout duration - show warning
                    this.startLogoutWarning();
                } else {
                    // Resume normal monitoring
                    this.startActivityMonitoring();
                }
            }
        },

        startLogoutWarning() {
            this.showLogoutWarning = true;
            this.warningActive = true;
            
            // Calculate precise remaining time based on actual activity
            const timeoutMinutes = this.$store.state.client.auto_logout_minutes;
            const timeoutMs = timeoutMinutes * 60 * 1000;
            const currentTime = Date.now();
            const timeSinceLastActivity = currentTime - this.lastActivityTime;
            const remainingMs = timeoutMs - timeSinceLastActivity;
            
            // Set countdown to remaining seconds (max 30 seconds)
            this.warningCountdown = Math.max(1, Math.min(30, Math.ceil(remainingMs / 1000)));
            
            // Start countdown timer
            this.warningTimer = setInterval(() => {
                this.warningCountdown--;
                if (this.warningCountdown <= 0) {
                    this.performAutoLogout();
                }
            }, 1000);
        },

        cancelLogoutWarning() {
            if (this.warningActive) {
                clearInterval(this.warningTimer);
                this.warningTimer = null;
                this.warningActive = false;
                this.showLogoutWarning = false;
                this.warningCountdown = 30;
                
                // Restart activity monitoring when user cancels warning
                this.startActivityMonitoring();
            }
        },

        async performAutoLogout() {
            try {
                // Clean up warning system
                this.cancelLogoutWarning();
                
                // Show logout message
                this.showLogoutMessage = true;
                
                // Check if safe key should be deleted (default to false if NULL)
                if (this.$store.state.client.delete_safe_key_on_logout) {
                    localStorage.removeItem('data_key');
                    localStorage.removeItem('salt');
                    this.$store.commit('clearAesKeys');
                }
                
                // Perform logout
                await supabase.auth.signOut();
                
                // Clean up activity tracking
                this.cleanupActivityTracking();
                
                // Redirect to login page
                if (this.$route.path !== '/') {
                    this.$router.push('/');
                }
            } catch (error) {
                // If session is already expired (403 error), that's fine - continue with cleanup
                if (error.message && error.message.includes('session missing')) {
                    console.log('Session already expired, proceeding with cleanup');
                } else {
                    console.error('Error during auto-logout:', error);
                }
                
                // Clean up even if logout fails
                this.cleanupActivityTracking();
                
                // Still redirect to login page
                if (this.$route.path !== '/') {
                    this.$router.push('/');
                }
            }
        },

        cleanupActivityTracking() {
            // Remove event listeners
            this.activityEvents.forEach(event => {
                window.removeEventListener(event, this.updateActivityTime);
            });
            
            // Remove Page Visibility API listener
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
            
            // Clear timers
            clearTimeout(this.activityTimer);
            this.activityTimer = null;
            
            // Clean up warning system
            this.cancelLogoutWarning();
        },
    },
};
</script>

<style scoped>
.gradient-border-container-GERINGFÜGIG, .gradient-border-container-TEILZEIT, .gradient-border-container-VOLLZEIT{
  position: relative;
  display: inline-block;
  border-radius: 24px; /* Dieser Wert sollte dem border-radius des v-chip entsprechen */
}

.gradient-border-container-GERINGFÜGIG::before {
  content: '';
  position: absolute;
  top: -1px; /* Anpassung basierend auf der Dicke des Rahmens */
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #EEEEEE, #A9B8C3);
  z-index: -1;
  border-radius: inherit; /* Erbt den border-radius des übergeordneten Containers */
}

.gradient-border-container-TEILZEIT::before {
  content: '';
  position: absolute;
  top: -1px; /* Anpassung basierend auf der Dicke des Rahmens */
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #DBECC0, #81C886);
  z-index: -1;
  border-radius: inherit; /* Erbt den border-radius des übergeordneten Containers */
}

.gradient-border-container-VOLLZEIT::before {
  content: '';
  position: absolute;
  top: -1px; /* Anpassung basierend auf der Dicke des Rahmens */
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #EDD1DE, #AFAFEC);
  z-index: -1;
  border-radius: inherit; /* Erbt den border-radius des übergeordneten Containers */
}

.gradient-chip {
  position: relative;
  z-index: 1;
  border-radius: 24px; /* Der tatsächliche border-radius des v-chip */
}
</style>

<style>
html {
    font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
        sans-serif;
    font-size: 16px;
    word-spacing: 1px;
    -ms-text-size-adjust: 100%;
    -webkit-text-size-adjust: 100%;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
    color: #2e495e;
    background-color: #f5f5f5 !important;
}

*,
*:before,
*:after {
    box-sizing: border-box;
    margin: 0;
}

.layout {
    /*height: 100vh;*/
    background: #f5f5f5 !important;
}
.navigation-container {
    background-color: #897f7c !important;
}

.content-container {
    background: #f5f5f5 !important;
}

.fixedBottom {
    position: fixed !important;
    bottom: 0 !important;
    width: 90%;
}
</style>
